import { Box, FormControl, MenuItem, Select, Typography } from "@mui/material";
import CheckedMark from "assets/icons/checked-mark.svg";
import UncheckedMark from "assets/icons/unchecked-mark.svg";
import { useEffect, useState } from "react";
import { durationPickerStyles } from "styles/common/durationPicker.styles";

interface DurationPickerProps {
  initialSelectedHours?: number | null;
  initialSelectedMinutes?: number | null;
  onChange: (hours: number | null, minutes: number | null) => void;
  duration_applicable?: boolean;
  handleDurationApplicableChange: (checked: boolean) => void;
  errors?: { duration_hours?: string; duration_minutes?: string };
  touched?: { duration_hours?: boolean; duration_minutes?: boolean };
}

const hoursList = Array.from({ length: 25 }, (_, i) => ({
  label: `${i} ${i === 1 ? "hr" : "hrs"}`,
  value: i,
}));

const minutesList = [
  { label: "15 mins", value: 15 },
  { label: "30 mins", value: 30 },
  { label: "45 mins", value: 45 },
];

const DurationPicker = ({
  initialSelectedHours,
  initialSelectedMinutes,
  onChange,
  duration_applicable,
  handleDurationApplicableChange,
  errors,
  touched,
}: DurationPickerProps) => {
  const [selectedHours, setSelectedHours] = useState<number | null>(
    initialSelectedHours ?? null
  );
  const [selectedMinutes, setSelectedMinutes] = useState<number | null>(
    initialSelectedMinutes ?? null
  );

  useEffect(() => {
    if (!duration_applicable) {
      setSelectedHours(null);
      setSelectedMinutes(null);
    }
  }, [duration_applicable]);

  return (
    <Box>
      <Box sx={durationPickerStyles.row}>
        <Box sx={durationPickerStyles.dropdownContainer}>
          <Typography style={durationPickerStyles.label}>Hours</Typography>
          <Box mt={1}>
            <FormControl
              sx={{ flex: 1, width: "100%" }}
              disabled={!duration_applicable}
            >
              <Select
                value={selectedHours ?? ""}
                displayEmpty
                onChange={(e) => {
                  const value =
                    e.target.value === "" ? null : Number(e.target.value);
                  setSelectedHours(value);
                  onChange(value, selectedMinutes);
                }}
              >
                {hoursList.map((hour) => (
                  <MenuItem key={hour.value} value={hour.value}>
                    {hour.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {errors?.duration_hours && touched?.duration_hours && (
            <Typography color="red" fontSize="12px">
              {errors.duration_hours}
            </Typography>
          )}
        </Box>
        <Box sx={durationPickerStyles.dropdownContainer}>
          <Typography style={durationPickerStyles.label}>Minutes</Typography>
          <Box mt={1}>
            <FormControl
              sx={{ flex: 1, width: "100%" }}
              disabled={!duration_applicable}
            >
              <Select
                value={selectedMinutes ?? ""}
                displayEmpty
                onChange={(e) => {
                  const value =
                    e.target.value === "" ? null : Number(e.target.value);
                  setSelectedMinutes(value);
                  onChange(selectedHours, value);
                }}
              >
                {minutesList.map((minute) => (
                  <MenuItem key={minute.value} value={minute.value}>
                    {minute.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {errors?.duration_minutes && touched?.duration_minutes && (
            <Typography color="red" fontSize="12px">
              {errors.duration_minutes}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={durationPickerStyles.checkboxContainer}>
        <Box
          onClick={() => handleDurationApplicableChange(!duration_applicable)}
          sx={durationPickerStyles.checkboxPressable}
        >
          {!duration_applicable ? (
            <img src={CheckedMark} alt="checked-mark" width={18} height={18} />
          ) : (
            <img
              src={UncheckedMark}
              alt="checked-mark"
              width={18}
              height={18}
            />
          )}
          <Typography sx={durationPickerStyles.checkboxText}>
            Not Applicable
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DurationPicker;
